import * as Sentry from '@sentry/react';
import { Navigate, type RouteObject, createBrowserRouter } from 'react-router';

import { Main } from '@/components';
import {
  applicationsRoute,
  connectAuthorizeRoute,
  logoutPathname,
  mcpRoute,
  mockApisRoute,
  notAllowedPathname,
  uploadImageRoute,
} from '@/constants';
import {
  AuthLayoutOutlet,
  ForgotPassword,
  LogOut,
  Login,
  NotAllowed,
  NotFound,
  RequireLoggedIn,
  RequireLoggedOut,
  ResetPassword,
  WhoAmI,
} from '@/screens';
import { configureDevRoutes } from '@/screens/dev';

const applicationsScreens = () => import('@/screens/applications');
const mockApiScreens = () => import('@/screens/mockApi');
const mcpScreens = () => import('@/screens/mcp');
const imageUploadScreens = () => import('@/screens/imageUploads');

export const routes: RouteObject[] = [
  {
    element: <WhoAmI />,
    hydrateFallbackElement: null,
    children: [
      {
        path: '/',
        element: (
          <RequireLoggedIn>
            <Main />
          </RequireLoggedIn>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={applicationsRoute} replace />,
          },
          {
            path: applicationsRoute,
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await applicationsScreens())
                    .ApplicationsCatalogue,
                }),
              },
              {
                path: 'mine',
                lazy: async () => ({
                  Component: (await applicationsScreens())
                    .ApplicationsCatalogue,
                }),
              },
              {
                path: ':id',
                lazy: async () => ({
                  Component: (await applicationsScreens()).ApplicationDetail,
                }),
              },
              {
                path: ':id/configure',
                lazy: async () => ({
                  Component: (await applicationsScreens()).ApplicationConfigure,
                }),
              },
            ],
          },
          {
            path: mockApisRoute,
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await mockApiScreens()).MockSystemsList,
                }),
              },
              {
                path: ':id',
                lazy: async () => ({
                  Component: (await mockApiScreens()).MockSystemDetail,
                }),
              },
              {
                path: ':id/endpoints/:endpointId',
                lazy: async () => ({
                  Component: (await mockApiScreens()).MockSystemDetail,
                }),
              },
            ],
          },
          {
            path: mcpRoute,
            lazy: async () => ({
              Component: (await mcpScreens()).McpConnections,
            }),
          },
          {
            path: notAllowedPathname,
            element: <NotAllowed />,
          },
          {
            path: '*',
            element: <NotFound />,
          },
        ],
      },

      {
        element: (
          <RequireLoggedIn>
            <AuthLayoutOutlet />
          </RequireLoggedIn>
        ),
        children: [
          {
            path: connectAuthorizeRoute,
            lazy: async () => ({
              Component: (await mcpScreens()).ConsentScreen,
            }),
          },
          {
            path: uploadImageRoute,
            lazy: async () => ({
              Component: (await imageUploadScreens()).UploadImageScreen,
            }),
          },
        ],
      },

      // auth
      {
        element: (
          <RequireLoggedOut>
            <AuthLayoutOutlet />
          </RequireLoggedOut>
        ),
        children: [
          { path: 'login', element: <Login /> },
          { path: logoutPathname, element: <LogOut /> },
          { path: 'forgot-password', element: <ForgotPassword /> },
          {
            path: 'reset-password',
            element: <ResetPassword />,
            children: [{ path: ':uidb64/:token' }],
          },
        ],
      },
    ],
  },
];

if (import.meta.env.DEV) configureDevRoutes(routes);

/** https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/ */
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter(routes, {
  basename: import.meta.env.BASE_URL,
});

export default router;
